import React from 'react';

import moment from 'moment-tz';
import {useSelector} from 'react-redux';
import {Progress} from 'reactstrap';
import {ProcedureTaskCore, ProcessTaskCore} from 'sweet-taskinfo/dist';

import {
  RoleAvatar,
  TeamAvatar,
  UserAvatar,
} from 'app/components/general/avatars';
import {CurrentUserSelector} from 'app/current-user/api';
import {ForegroundDiv} from 'app/procedures/styles';
import {MarkCompletedButton} from 'app/tasks/components/ProcedureTaskPage/MarkCompletedButton';
import {getRepetitionLabel, isOverdue, repeats} from 'app/tasks/utils';
import {gt, ngt} from 'app/utils/i18n';
import {onlyUpdateForKeys} from 'app/utils/react-utils';

import {TaskInstanceSerializedType} from 'sweet-types';

type Props = {
  taskCore: ProcedureTaskCore | ProcessTaskCore;
  isTaskBlocked?: boolean;
  hasDescription: boolean;
  updateTaskInstance: (
    task: TaskInstanceSerializedType,
    changes: any
  ) => Promise<any>;
  setWarnIncomplete: (status: boolean) => any;
};

function BaseViewTaskMetaDataExtra({
  updateTaskInstance,
  hasDescription,
  setWarnIncomplete,
  taskCore,
  isTaskBlocked,
}: Props) {
  const {task} = taskCore;
  const userTimezone = useSelector(
    (state) => CurrentUserSelector(state).data!.timezone
  );
  const taskDateTzed =
    task.due !== undefined ? (
      <span title={moment(task.due).format()}>
        {moment(task.due).tz(userTimezone).format('lll')}
      </span>
    ) : (
      ''
    );
  let stats: {
    canBeCompleted?: boolean;
    completedItems?: number;
    percentComplete?: number | null;
    progressLabel?: string | null;
  } = {};

  if (taskCore.hasVersion === true) {
    const {completedPath, canBeCompleted} = taskCore.state.progressInfo;
    const {percentComplete} = taskCore;
    stats = {
      canBeCompleted,
      completedItems: completedPath.length,
      progressLabel:
        percentComplete != null ? gt('%d%% complete', percentComplete) : null,
      percentComplete,
    };
  }

  const taskIsOverdue = isOverdue(task);
  const repetitionLabel = getRepetitionLabel(task.template.rrule);
  const taskRepeats = repeats(task.template.rrule);
  return (
    <div className="mt-3">
      {hasDescription ? <hr /> : null}
      <div className="row">
        <div className="col-12 mt-1 mb-md-2 col-md-auto">
          {gt('Task assigned to')}
          <div>
            {task.template.users.map((user) => (
              <div className="mr-2 mt-1 float-left" key={'user-' + user.id}>
                <UserAvatar
                  showCard
                  user={user}
                  isGuest={user.is_guest_until != null}
                />
              </div>
            ))}
            {task.template.teams.map((team) => (
              <div className="mr-2 mt-1 float-left" key={'team-' + team.id}>
                <TeamAvatar link={true} team={team} />
              </div>
            ))}
            {task.template.roles.map((role) => (
              <div className="mr-2 mt-1 float-left" key={'role-' + role.id}>
                <RoleAvatar link={true} role={role} />
              </div>
            ))}
          </div>
        </div>
        <div className="col-12 mb-md-2 col-md-auto ml-auto">
          {gt('Task assigned by')}
          <div>
            <div className="ml-2 mt-1 float-right">
              <UserAvatar user={task.template.assigner} showCard />
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <p className="col-12 col-md-auto text-muted">
          {taskRepeats && <i className="far fa-clock" />}{' '}
          {gt('Repeats %(times)s', {
            times: repetitionLabel,
          })}
        </p>
        <p
          className={`col-12 col-md-auto ml-auto text-${
            taskIsOverdue ? 'danger' : 'muted'
          }`}
        >
          {taskIsOverdue ? (
            <span>
              <span className="badge badge-danger">
                {gt('Overdue').toUpperCase()}
              </span>{' '}
              {taskDateTzed}
            </span>
          ) : (
            <span>
              {gt('Due %(date)s', {
                date: taskDateTzed,
              })}
            </span>
          )}
        </p>
      </div>

      {stats.percentComplete != null && stats.progressLabel != null ? (
        <div className="row">
          <div className="col-12">
            <Progress
              className="bg-secondary mt-2 mb-3"
              color="dark"
              value={stats.percentComplete}
            >
              {stats.progressLabel}
            </Progress>
          </div>
        </div>
      ) : null}
      <div className="row align-items-center">
        <div className="col-12 col-md-auto text-muted mb-3 mb-md-0">
          {stats.completedItems != null
            ? ngt(
                'Completed %s step',
                'Completed %s steps',
                stats.completedItems
              )
            : null}
        </div>
        {taskCore.hasVersion === true || taskCore.isCompleted ? (
          <ForegroundDiv className="col-12 col-md-auto ml-auto">
            <MarkCompletedButton
              taskCore={taskCore}
              updateTaskInstance={updateTaskInstance}
              isTaskBlocked={isTaskBlocked}
              onBlockedClick={() => setWarnIncomplete(true)}
              className="float-right"
            />
            {taskCore.isCompleted ? (
              <div className="mr-2 float-left d-flex align-items-center">
                <div className="text-muted mr-1">{gt('Completed by')}</div>
                <UserAvatar showCard user={taskCore.completedBy ?? null} />
              </div>
            ) : null}
          </ForegroundDiv>
        ) : null}
      </div>
    </div>
  );
}

export const ViewTaskMetaDataExtra = onlyUpdateForKeys<Props>(
  BaseViewTaskMetaDataExtra,
  ['taskCore', 'warnIncomplete', 'isTaskBlocked']
);
